import { differenceInDays, isSameDay, set, startOfDay, subDays } from 'date-fns';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { GetRegisterClassesWithNAVResponse } from 'api';

dayjs.extend(utc);

// type normaliseNavDataType = (data?: GetRegisterClassesWithNAVResponse) => (ApexAxisChartSeries & { id: number })[];
export interface NormaliseNavData {
  name: string;
  id: number;
  data: {
    x: string;
    y: number;
  }[];
}

export const normaliseNavData = (
  data?: GetRegisterClassesWithNAVResponse,
  withCurrentDate = true,
): NormaliseNavData[] => {
  if (!data?.data) return [];

  const allTimePoints = (data?.data || []).reduce(
    (acc, { navs = [] }) => [
      ...acc,
      ...(navs || [])
        .sort((a, b) => new Date(a.changes_date).getTime() - new Date(b.changes_date).getTime())
        .filter(({ price_per_share }, index, arr) => {
          const prevElement = arr[index - 1];

          return (
            index === 0 ||
            index === arr.length - 1 ||
            price_per_share !== prevElement?.price_per_share
          );
        })
        .map(({ changes_date }) => changes_date),
    ],
    [] as string[],
  );

  let uniqSortedTimePoints = [...new Set(allTimePoints)].sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  );

  if (uniqSortedTimePoints.length === 1) {
    const now = new Date();
    uniqSortedTimePoints[0] = set(new Date(uniqSortedTimePoints[0]), {
      hours: now.getHours(),
      minutes: now.getMinutes(),
      seconds: now.getSeconds(),
      milliseconds: now.getMilliseconds(),
    }).toString();

    uniqSortedTimePoints.unshift(startOfDay(new Date(uniqSortedTimePoints[0])).toString());
    // uniqSortedTimePoints.unshift(subDays(new Date(uniqSortedTimePoints[0]), 1).toString());
  }

  const lastDate =
    uniqSortedTimePoints?.at(-1) &&
    !isSameDay(new Date(uniqSortedTimePoints?.at(-1) || subDays(new Date(), 1)), new Date()) &&
    differenceInDays(new Date(uniqSortedTimePoints?.at(-1) || subDays(new Date(), 1)), new Date()) <
      0 &&
    withCurrentDate
      ? startOfDay(new Date()).toString()
      : undefined;

  uniqSortedTimePoints = lastDate ? [...uniqSortedTimePoints, lastDate] : uniqSortedTimePoints;

  const chartDate: NormaliseNavData[] = (data?.data || [])
    .filter(({ navs }) => navs?.length)
    .map(({ class_name, id, navs = [] }) => {
      const lastPrice = lastDate ? navs.at(-1) : undefined;
      return {
        name: class_name,
        id,
        data: uniqSortedTimePoints.map(date => {
          const dateObj = dayjs.utc(date);

          const isMidnightUTC =
            dateObj.format('H:m:s') === '0:0:0' ||
            dateObj.format('H:m:s') === '3:0:0' ||
            dateObj.format('H:m:s') === '2:0:0';

          const formattedDate = isMidnightUTC
            ? dateObj.format('DD-MM-YYYY')
            : dateObj.format('DD-MM-YYYY HH:mm:ss');

          return {
            x: formattedDate,
            y:
              lastPrice &&
              lastDate &&
              dayjs.utc(lastDate).format('DD-MM-YYYY') === dateObj.format('DD-MM-YYYY')
                ? lastPrice?.price_per_share
                : navs.find(
                    ({ changes_date }) =>
                      dayjs.utc(changes_date).format('DD-MM-YYYY HH:mm:ss') ===
                      dayjs.utc(date).format('DD-MM-YYYY HH:mm:ss'),
                  )?.price_per_share ||
                  navs.find(
                    ({ changes_date }) =>
                      dayjs.utc(changes_date).format('DD-MM-YYYY') ===
                      dayjs.utc(date).format('DD-MM-YYYY'),
                  )?.price_per_share ||
                  0,
          };
        }),
      };
    });

  return chartDate;
};
